import React from 'react'
import { useTranslation } from 'react-i18next'
import { Cookie, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useCommunity } from '@/lib/CommunityContext'
import { useHasCookieChoice } from './useHasCookieChoice'
import { OPEN_COOKIE_NOTICE_EVENT, readCookieChoice, saveCookieChoice, type CookieChoice } from '@/lib/cookieConsent'

/**
 * F-201: the cookie notice. Shown on the first visit of a community that set
 * its own Google Analytics, and again from "Cookies" (side menu, profile
 * panel). "Aceitar" and "Recusar" are the same size; "Personalizar" lists the
 * necessary cookies (always on) and the measurement switch. A community
 * without GA never shows it. The GA itself waits for the choice in
 * `useGoogleAnalytics`.
 */
export const CookieNotice = () => {
  const { t } = useTranslation('common')
  const { community } = useCommunity()
  const enabled = useHasCookieChoice()
  const communityId = community?.id ?? null
  const [open, setOpen] = React.useState(false)
  const [current, setCurrent] = React.useState<CookieChoice | null>(null)
  const [customizing, setCustomizing] = React.useState(false)
  const [analytics, setAnalytics] = React.useState(false)
  const titleId = React.useId()

  React.useEffect(() => {
    if (!enabled) {
      setOpen(false)
      return
    }
    const stored = readCookieChoice(communityId)
    setCurrent(stored)
    setOpen(stored === null)
  }, [enabled, communityId])

  React.useEffect(() => {
    const reopen = () => {
      if (!enabled) return
      const stored = readCookieChoice(communityId)
      setCurrent(stored)
      setAnalytics(stored === 'granted')
      setCustomizing(false)
      setOpen(true)
    }
    window.addEventListener(OPEN_COOKIE_NOTICE_EVENT, reopen)
    return () => window.removeEventListener(OPEN_COOKIE_NOTICE_EVENT, reopen)
  }, [enabled, communityId])

  if (!enabled || !open || !communityId) return null

  const decide = (choice: CookieChoice) => {
    saveCookieChoice(communityId, choice)
    setCurrent(choice)
    setOpen(false)
    setCustomizing(false)
  }

  const name = community?.name || ''

  return (
    <section
      role="dialog"
      aria-labelledby={titleId}
      className="fixed inset-x-4 bottom-4 z-[70] mx-auto max-w-lg rounded-xl border border-gray-200 bg-white p-4 shadow-xl sm:left-4 sm:right-auto sm:mx-0 sm:p-5"
      data-testid="cookie-notice"
    >
      <div className="flex items-start gap-3">
        <Cookie className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="text-sm font-semibold text-gray-900">{t('cookies.title')}</h2>
          <p className="mt-1 text-sm text-gray-600">{t('cookies.body', { community: name })}</p>
        </div>
        {current !== null && (
          <button type="button" onClick={() => setOpen(false)} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label={t('close')} data-testid="cookie-notice-close">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {customizing && (
        <ul className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-200" data-testid="cookie-notice-options">
          <li className="flex items-start justify-between gap-4 p-3">
            <label htmlFor="cookie-necessary" className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">{t('cookies.necessary')}</span>
              <span className="block text-xs text-gray-500">{t('cookies.necessaryHelp')}</span>
            </label>
            <Switch id="cookie-necessary" checked disabled aria-readonly className="data-[state=checked]:bg-primary" />
          </li>
          <li className="flex items-start justify-between gap-4 p-3">
            <label htmlFor="cookie-analytics" className="min-w-0 cursor-pointer">
              <span className="block text-sm font-medium text-gray-900">{t('cookies.analytics')}</span>
              <span className="block text-xs text-gray-500">{t('cookies.analyticsHelp', { community: name })}</span>
            </label>
            <Switch id="cookie-analytics" checked={analytics} onCheckedChange={setAnalytics} className="data-[state=checked]:bg-primary" data-testid="cookie-analytics-switch" />
          </li>
        </ul>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" className="w-full" onClick={() => decide('denied')} data-testid="cookie-reject">
          {t('cookies.reject')}
        </Button>
        <Button type="button" variant="outline" className="w-full" onClick={() => decide('granted')} data-testid="cookie-accept">
          {t('cookies.accept')}
        </Button>
      </div>
      <div className="mt-2 flex justify-center">
        {customizing ? (
          <Button type="button" variant="link" size="sm" onClick={() => decide(analytics ? 'granted' : 'denied')} data-testid="cookie-save">
            {t('cookies.save')}
          </Button>
        ) : (
          <Button type="button" variant="link" size="sm" onClick={() => { setAnalytics(current === 'granted'); setCustomizing(true) }} data-testid="cookie-customize">
            {t('cookies.customize')}
          </Button>
        )}
      </div>
    </section>
  )
}
